"use client";

import Link from "next/link";

import cn from "classnames";

import { useTagging } from "@/hooks/useTagging";
import type { RichText as RichTextType } from "@/sanity.types";

import { ProductCardActions } from "./productCardActions.component";
import { trackRecommendedProductSelect } from "./productCardAnalytics";
import { ProductCardContent } from "./productCardContent.component";
import { ProductCardImage } from "./productCardImage.component";
import { ProductCardLinkPendingIndicator } from "./productCardLinkPendingIndicator.component";
import { ProductCardOverlays } from "./productCardOverlays.component";

export const ProductCard = ({
  product,
  className,
  badges,
  index,
}: ProductCardProps) => {
  const hasOverlays = badges && badges.length > 0;
  const [, setTag] = useTagging();

  const handleSelectItem = () => {
    trackRecommendedProductSelect(product, setTag, index);
  };

  return (
    <div
      className={cn(
        "relative flex flex-col gap-5 rounded-2xl border-2 border-white bg-white/5 p-6 shadow-inner lg:flex-row lg:items-stretch lg:py-14 lg:pr-8",
        className,
      )}>
      {product.slug ? (
        <Link
          href={product.slug ?? "#"}
          aria-label={`View ${product.name} product details`}
          onClick={handleSelectItem}
          className="focus-visible:ring-secondary absolute inset-0 z-20 rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:hidden">
          <ProductCardLinkPendingIndicator />
        </Link>
      ) : null}
      {hasOverlays && <ProductCardOverlays badges={badges} />}
      {product.featuredImage && (
        <ProductCardImage featuredImage={product.featuredImage} />
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-4 overflow-hidden lg:gap-5">
        <ProductCardContent product={product} />
        <ProductCardActions className="flex" product={product} index={index} />
      </div>
    </div>
  );
};

export type ProductCardProduct = {
  id?: string;
  slug?: string;
  name: string;
  shortDescription?: string | null;
  listPrice?: number | null;
  salesPrice?: number | null;
  featuredImage?: { url: string | null; alt?: string | null } | null;
  legal?: RichTextType | null;
  additionalLegal?: RichTextType | null;
  type?: string | null;
  monitoringPlan?: {
    id?: string | null;
    name?: string | null;
    listPrice?: number | null;
    salesPrice?: number | null;
  } | null;
};

export type ProductCardProps = {
  product: ProductCardProduct;
  className?: string;
  badges?: string[];
  index?: number;
};
